import { FC } from "react";

import { MuiBox, MuiStepper, MuiTypography } from "../../../../components";
import { BULK_TYPE, SummaryStatus } from "../../../../helpers/constants";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { bulkCreateProgramSiteCoreKeys } from "../../siteCore/siteCoreKeys";
import { BulkCreateProgramStepperWrapper } from "./BulkCreateProgramStepper.style";

interface BulkCreateProgramStepperProps {
  currentStep: number;
  isAllProceed?: boolean;
  isUploadProceed?: boolean;
  status?: string;
  isEditBulkProgram?: boolean;
}

export const BulkCreateProgramStepper: FC<BulkCreateProgramStepperProps> = ({
  currentStep,
  isAllProceed = false,
  isUploadProceed = false,
  status = "",
  isEditBulkProgram = false,
}) => {
  const { useFetchLabel } = useSiteCore();
  const getLabel = useFetchLabel(bulkCreateProgramSiteCoreKeys.progressBar);

  const processingSubHeaderLabel = isEditBulkProgram
    ? getLabel(bulkCreateProgramSiteCoreKeys.weAreProcessingYourFileMessage)
    : getLabel(bulkCreateProgramSiteCoreKeys.processingYourFileMessage);

  const getStepperLabels = () => {
    let header = "";
    let subHeader = "";

    switch (currentStep) {
      case 0:
        header = isUploadProceed
          ? getLabel(bulkCreateProgramSiteCoreKeys.youareAlmostDone)
          : getLabel(bulkCreateProgramSiteCoreKeys.letsStart);
        subHeader = isUploadProceed
          ? getLabel(bulkCreateProgramSiteCoreKeys.proceedToProcessYourFile)
          : getLabel(
              bulkCreateProgramSiteCoreKeys.bulkCreateProgramInitialMessage,
            );
        break;

      case 1:
        header = getLabel(bulkCreateProgramSiteCoreKeys.pleaseWait);
        subHeader =
          status === SummaryStatus.QUEUE
            ? getLabel(bulkCreateProgramSiteCoreKeys.fileInQueueWaitMessage)
            : processingSubHeaderLabel;
        break;

      case 2:
      case 3:
        header = isAllProceed
          ? getLabel(bulkCreateProgramSiteCoreKeys.youareDone)
          : getLabel(bulkCreateProgramSiteCoreKeys.youareDoneHoldOn);
        subHeader = isAllProceed
          ? ""
          : getLabel(bulkCreateProgramSiteCoreKeys.unProcessedProgramsMessage);
        break;

      default:
        header = getLabel(bulkCreateProgramSiteCoreKeys.letsStart);
        subHeader = getLabel(
          bulkCreateProgramSiteCoreKeys.bulkCreateProgramInitialMessage,
        );
        break;
    }

    return { header, subHeader };
  };

  const getLabelForStep = (step: number): string => {
    switch (step) {
      case 0:
        return getLabel(bulkCreateProgramSiteCoreKeys.process);
      case 1:
        return status === SummaryStatus.QUEUE
          ? getLabel(bulkCreateProgramSiteCoreKeys.inQueueProgressState)
          : getLabel(bulkCreateProgramSiteCoreKeys.processingProgressState);
      default:
        return getLabel(bulkCreateProgramSiteCoreKeys.processed);
    }
  };

  return (
    <BulkCreateProgramStepperWrapper data-testid="bulk-create-program-stepper">
      <MuiStepper
        activeStep={currentStep === 2 ? 3 : currentStep}
        status={status}
        steps={[
          {
            title: getLabel(bulkCreateProgramSiteCoreKeys.uploadProgressState),
          },
          {
            title: getLabelForStep(currentStep),
          },
          {
            title: getLabel(bulkCreateProgramSiteCoreKeys.successProgressState),
          },
        ]}
        bulkType={BULK_TYPE.PROGRAM}
      />

      <MuiBox className="stepper-content">
        <MuiTypography variant="h1Bold" className="stepper-title">
          {getStepperLabels().header}
        </MuiTypography>
        <MuiTypography
          variant="h2Bold"
          className="stepper-description"
          component="div"
        >
          {getStepperLabels().subHeader}
        </MuiTypography>
      </MuiBox>
    </BulkCreateProgramStepperWrapper>
  );
};
